/**
 * DocumentUploader Component
 *
 * Reusable document upload UI — file upload only.
 * Paste text is a separate ingestion path.
 */

import { useState, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FileUp, Loader2, AlertCircle } from 'lucide-react'
import type { ContentType } from '@/lib/api/client'
import { CONTENT_TYPES, CONTENT_TYPE_ICONS } from '@/lib/analyzer/types'
import { extractTextFromPdf } from '@/lib/pdf/extractor'
import { ExtractionProgress } from './ExtractionProgress'
import type { Document2WithEntities, Document2Entity } from '@/lib/api/client'
import type { ExtractionProgress as ExtractionProgressType } from '@/hooks/useDocumentProcessing'

interface DocumentUploaderProps {
  onUpload: (file: File, contentType: ContentType, title?: string) => Promise<Document2WithEntities>
  onExtract: (docId: string, useCache?: boolean) => Promise<{ entities: Document2Entity[]; fromCache: boolean }>
  extractionProgress: ExtractionProgressType
  isConfigured: boolean
  onComplete?: (doc: Document2WithEntities) => void
  error?: Error | null
  onClose?: () => void
  className?: string
  /** @deprecated Paste is now a separate path — this prop is ignored */
  onPaste?: unknown
}

export function DocumentUploader({
  onUpload,
  onExtract,
  extractionProgress,
  isConfigured,
  onComplete,
  error,
  onClose,
  className = '',
}: DocumentUploaderProps) {
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [contentType, setContentType] = useState<ContentType>('custom')
  const [file, setFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [fileError, setFileError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const isProcessing = loading || extractionProgress.stage !== 'idle'

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return

    setFile(selectedFile)
    setFileError(null)
    setLoading(true)

    try {
      const ext = selectedFile.name.split('.').pop()?.toLowerCase()

      if (ext === 'pdf') {
        const extracted = await extractTextFromPdf(selectedFile)
        setContent(extracted.fullText)
      } else {
        const text = await selectedFile.text()
        setContent(text)
      }

      if (!title) {
        setTitle(selectedFile.name.replace(/\.[^.]+$/, ''))
      }
    } catch (err) {
      setFileError(err instanceof Error ? err.message : 'Failed to read file')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    if (!isConfigured) {
      setFileError('Claude API key not configured')
      return
    }

    if (!file) {
      setFileError('Please select a file')
      return
    }

    if (!title.trim()) {
      setFileError('Title is required')
      return
    }

    if (!content.trim() || content.length < 50) {
      setFileError('File content must be at least 50 characters')
      return
    }

    try {
      setLoading(true)
      setFileError(null)

      const doc = await onUpload(file, contentType, title.trim())
      await onExtract(doc.document.id, true)
      onComplete?.(doc)

      setTitle('')
      setContent('')
      setFile(null)
    } catch (err) {
      setFileError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* File Drop Zone */}
      <div
        className={`border-2 border-dashed border-warm-200 rounded-lg p-8 text-center transition-colors ${
          isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-warm-400'
        }`}
        onClick={() => !isProcessing && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".txt,.md,.markdown,.pdf"
          onChange={handleFileSelect}
          className="hidden"
          disabled={isProcessing}
        />
        {file ? (
          <div>
            <FileUp className="h-8 w-8 mx-auto mb-2 text-solution" />
            <p className="font-medium">{file.name}</p>
            <p className="text-sm text-text-secondary">
              {(file.size / 1024).toFixed(1)} KB
            </p>
          </div>
        ) : (
          <div>
            <FileUp className="h-8 w-8 mx-auto mb-2 text-warm-400" />
            <p className="text-text-secondary">Click to select a file or drag and drop</p>
            <p className="text-xs text-text-tertiary mt-1">Supported: .pdf, .txt, .md</p>
          </div>
        )}
      </div>

      {/* File Preview */}
      {content && (
        <div className="space-y-2">
          <Label>Preview</Label>
          <div className="border rounded-lg p-4 max-h-48 overflow-y-auto bg-warm-50">
            <pre className="text-sm whitespace-pre-wrap font-mono">
              {content.slice(0, 1000)}
              {content.length > 1000 && '...'}
            </pre>
          </div>
          <p className="text-xs text-text-tertiary">{content.length} characters loaded</p>
        </div>
      )}

      {/* Title */}
      <div className="space-y-2">
        <Label htmlFor="title">Title</Label>
        <Input
          id="title"
          placeholder="Auto-filled from file name"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={isProcessing}
        />
      </div>

      {/* Content Type */}
      <div className="space-y-2">
        <Label htmlFor="contentType">Content Type</Label>
        <Select
          value={contentType}
          onValueChange={(v) => setContentType(v as ContentType)}
          disabled={isProcessing}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select content type" />
          </SelectTrigger>
          <SelectContent>
            {CONTENT_TYPES.map((ct) => {
              const IconComponent = CONTENT_TYPE_ICONS[ct.icon]
              return (
                <SelectItem key={ct.id} value={ct.id}>
                  <span className="flex items-center gap-2">
                    {IconComponent && <IconComponent className="h-4 w-4 text-zinc-500" />}
                    <span>{ct.name}</span>
                  </span>
                </SelectItem>
              )
            })}
          </SelectContent>
        </Select>
      </div>

      {/* Extraction Progress */}
      {extractionProgress.stage !== 'idle' && (
        <ExtractionProgress progress={extractionProgress} />
      )}

      {/* Error Message */}
      {(fileError || error) && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-700">{fileError || error?.message}</div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 pt-4">
        {onClose && (
          <Button variant="outline" onClick={onClose} disabled={isProcessing}>
            Cancel
          </Button>
        )}
        <Button
          onClick={handleSubmit}
          disabled={isProcessing || !file || !title.trim() || !content.trim() || content.length < 50}
          className="bg-solution hover:bg-solution/90"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            'Extract & store'
          )}
        </Button>
      </div>
    </div>
  )
}
